import { unzipSync, strFromU8 } from 'fflate';

/**
 * Leitura de `.xlsx`.
 *
 * Um `.xlsx` é um zip de XML, e o sistema já descompacta zip para o backup (`fflate`).
 * Ler a primeira planilha custa este arquivo; trazer uma biblioteca de planilha custaria
 * algumas centenas de kB no pacote de uma aplicação que roda offline, a partir de uma
 * pasta local. O preço não se justifica para ler um cadastro de materiais.
 *
 * O que é suportado: primeira planilha, texto compartilhado (`sharedStrings`), texto
 * embutido (`inlineStr`), números e as fórmulas **já calculadas** — que é o que o Excel
 * grava junto. O que não é: fórmula sem valor calculado, que vem vazia, e data como
 * número de série, que chega como número. Nenhum dos dois aparece em cadastro de item.
 *
 * A célula com formato de **porcentagem** (`0%`, `0,00%`) guarda a fração no arquivo: o 8% que
 * a pessoa digitou está lá como 0,08. Devolver o valor cru faria uma perda de 8% entrar como
 * 0,08%, em silêncio. A leitura olha o formato da célula (`styles.xml`) e devolve o texto que
 * a tela mostra — "8%" —, que é como quem preenche o escreveria.
 */

interface SheetCell {
  readonly column: number;
  readonly value: string;
}

export function decodeEntities(value: string): string {
  return value
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, code: string) => String.fromCodePoint(Number(code)))
    .replace(/&amp;/g, '&');
}

/** Junta todos os `<t>` de um `<si>`: texto com formatação vem quebrado em vários. */
export function parseSharedStrings(xml: string): string[] {
  const strings: string[] = [];
  const itemPattern = /<si>([\s\S]*?)<\/si>/g;
  let item: RegExpExecArray | null;

  while ((item = itemPattern.exec(xml)) !== null) {
    const chunks = [...(item[1] ?? '').matchAll(/<t[^>]*>([\s\S]*?)<\/t>/g)].map(
      (match) => match[1] ?? '',
    );
    strings.push(decodeEntities(chunks.join('')));
  }

  return strings;
}

/** `"BC12"` → 1 (índice da coluna B, base zero). */
export function columnIndex(reference: string): number {
  const letters = /^([A-Z]+)/.exec(reference)?.[1] ?? 'A';
  let index = 0;
  for (const letter of letters) {
    index = index * 26 + (letter.charCodeAt(0) - 64);
  }
  return index - 1;
}

/** Os formatos internos de porcentagem do Excel: `0%` e `0,00%`. */
const FORMATOS_INTERNOS_DE_PORCENTAGEM: ReadonlySet<number> = new Set([9, 10]);

/** O código de formato escreve `%` fora de aspas e de barra invertida? */
function formatoTemPorcentagem(codigo: string): boolean {
  const barra = String.fromCharCode(92);
  const semLiterais = codigo
    .replace(/"[^"]*"/g, '')
    .split(barra)
    .map((pedaco, indice) => (indice === 0 ? pedaco : pedaco.slice(1)))
    .join('');
  return semLiterais.includes('%');
}

/**
 * Os estilos de célula (`s="…"`) cujo formato é porcentagem.
 *
 * O estilo é a posição do `<xf>` dentro de `cellXfs`; o formato dele é um número interno
 * (9 e 10 são as porcentagens) ou um `numFmtId` personalizado, declarado em `numFmts` com o
 * código por extenso.
 */
export function parseEstilosDePorcentagem(xml: string): ReadonlySet<number> {
  const personalizados = new Set<number>();
  for (const achado of xml.matchAll(/<numFmt\b([^>]*?)\/?>/g)) {
    const atributos = achado[1] ?? '';
    const id = /numFmtId="(\d+)"/.exec(atributos)?.[1];
    const codigo = /formatCode="([^"]*)"/.exec(atributos)?.[1];
    if (id !== undefined && codigo !== undefined && formatoTemPorcentagem(decodeEntities(codigo))) {
      personalizados.add(Number(id));
    }
  }

  const estilos = new Set<number>();
  const bloco = /<cellXfs\b[^>]*>([\s\S]*?)<\/cellXfs>/.exec(xml)?.[1] ?? '';
  let posicao = 0;
  for (const xf of bloco.matchAll(/<xf\b([^>]*?)\/?>/g)) {
    const formato = Number(/numFmtId="(\d+)"/.exec(xf[1] ?? '')?.[1] ?? 0);
    if (FORMATOS_INTERNOS_DE_PORCENTAGEM.has(formato) || personalizados.has(formato)) {
      estilos.add(posicao);
    }
    posicao += 1;
  }
  return estilos;
}

/** A fração do arquivo como a tela a mostra: `0.08` → `"8%"`, sem o ruído do ponto flutuante. */
function comoPorcentagem(valor: string): string {
  const numero = Number(valor);
  if (!Number.isFinite(numero)) return valor;
  return `${String(Math.round(numero * 100 * 1e9) / 1e9)}%`;
}

function estilosDe(files: Readonly<Record<string, Uint8Array>>): ReadonlySet<number> {
  const estilos = files['xl/styles.xml'];
  return estilos === undefined ? new Set() : parseEstilosDePorcentagem(strFromU8(estilos));
}

/**
 * Quantas linhas **com dado** uma aba pode ter.
 *
 * Linha vazia não conta: o Excel grava a linha formatada sem dado, e a planilha que formatou a
 * coluna inteira traz um milhão delas — que a leitura pula. Duzentas mil linhas com dado é dez
 * vezes a maior planilha de obra que passou por aqui (a de fundação da Veneza, com 12.674);
 * acima disso a aba é engano, e montar a tabela dela esgotaria a memória do navegador antes de
 * qualquer aviso.
 */
export const LINHAS_COM_DADO_POR_ABA = 200_000;

/** A aba passou do limite de linhas com dado: a leitura para ali, e a mensagem diz qual. */
export class PlanilhaGrandeDemais extends Error {
  constructor(
    readonly aba: string,
    readonly limite: number,
  ) {
    super(
      `A aba "${aba}" tem mais de ${limite.toLocaleString('pt-BR')} linhas com dado. ` +
        'Divida a planilha em arquivos menores, ou apague as linhas que não entram na importação.',
    );
    this.name = 'PlanilhaGrandeDemais';
  }
}

interface OpcoesDaAba {
  readonly porcentagens?: ReadonlySet<number>;
  readonly aba?: string;
  readonly limite?: number;
}

function parseSheet(xml: string, shared: readonly string[], opcoes: OpcoesDaAba = {}): string[][] {
  const porcentagens = opcoes.porcentagens ?? new Set<number>();
  const limite = opcoes.limite ?? LINHAS_COM_DADO_POR_ABA;
  const rows: string[][] = [];
  // A linha que se fecha sozinha (`<row r="12675" spans="1:9"/>`) vem **primeiro** na
  // alternância, e é pulada: é o jeito do Excel gravar a linha formatada sem dado. Pela ordem
  // de antes (`<row[^>]*>` e a busca preguiçosa por `</row>`), o `[^>]*` engolia a barra do
  // fecho e a busca corria até o fim do arquivo sem achar — uma vez por linha vazia. A planilha
  // de fundação da Veneza (22/10/2026) tem 12.674 linhas com dado e 1.035.868 vazias depois
  // delas: 46 MB percorridos um milhão de vezes, e o navegador congelava sem erro nenhum.
  const rowPattern = /<row\b[^>]*?\/>|<row\b[^>]*>([\s\S]*?)<\/row>/g;
  let rowMatch: RegExpExecArray | null;

  while ((rowMatch = rowPattern.exec(xml)) !== null) {
    const corpo = rowMatch[1];
    if (corpo === undefined || corpo === '') continue;
    const cells: SheetCell[] = [];
    // A célula vazia (`<c r="B2" s="16"/>`) vem **primeiro** na alternância, e é o que
    // impede o defeito mais silencioso deste arquivo: pela ordem inversa, `([^>]*)`
    // engolia a barra do fecho, o `>` seguinte casava, e a busca preguiçosa por `</c>`
    // atravessava as células vazias até a próxima com conteúdo. O valor dela reaparecia na
    // coluna da primeira vazia — a planilha entrava inteira, com as colunas escorregadas.
    const cellPattern = /<c([^>]*?)\/>|<c([^>]*)>([\s\S]*?)<\/c>/g;
    let cellMatch: RegExpExecArray | null;

    while ((cellMatch = cellPattern.exec(corpo)) !== null) {
      const attributes = cellMatch[1] ?? cellMatch[2] ?? '';
      const body = cellMatch[3] ?? '';
      const reference = /r="([A-Z]+\d+)"/.exec(attributes)?.[1] ?? 'A1';
      const type = /t="([^"]+)"/.exec(attributes)?.[1];

      let value = '';
      if (type === 's') {
        const index = Number(/<v>([\s\S]*?)<\/v>/.exec(body)?.[1] ?? '-1');
        value = shared[index] ?? '';
      } else if (type === 'inlineStr') {
        const chunks = [...body.matchAll(/<t[^>]*>([\s\S]*?)<\/t>/g)].map(
          (match) => match[1] ?? '',
        );
        value = decodeEntities(chunks.join(''));
      } else {
        value = decodeEntities(/<v>([\s\S]*?)<\/v>/.exec(body)?.[1] ?? '');
        const estilo = /\ss="(\d+)"/.exec(attributes)?.[1];
        const numerica = type === undefined || type === 'n';
        if (value !== '' && numerica && estilo !== undefined && porcentagens.has(Number(estilo))) {
          value = comoPorcentagem(value);
        }
      }

      // A célula vazia formatada (`<c r="I9" s="1"/>`) não abre coluna: sem isso, a linha de
      // células vazias até a última coluna da folha viraria dezesseis mil textos vazios.
      if (value !== '') cells.push({ column: columnIndex(reference), value });
    }

    if (cells.length === 0) continue;
    if (rows.length >= limite) throw new PlanilhaGrandeDemais(opcoes.aba ?? 'Planilha 1', limite);

    // Colunas vazias não aparecem no XML: a linha é montada por posição, senão os
    // valores escorregam para a coluna errada quando falta uma célula no meio.
    const width = Math.max(...cells.map((cell) => cell.column)) + 1;
    const row: string[] = Array.from({ length: width }, () => '');
    for (const cell of cells) row[cell.column] = cell.value;
    rows.push(row);
  }

  return rows.filter((row) => row.some((cell) => cell.trim() !== ''));
}

export interface XlsxContent {
  readonly rows: string[][];
  readonly sheetName: string;
}

export function readXlsx(bytes: Uint8Array): XlsxContent {
  const files = unzipSync(bytes);

  const sharedXml = files['xl/sharedStrings.xml'];
  const shared = sharedXml === undefined ? [] : parseSharedStrings(strFromU8(sharedXml));

  const sheetPath =
    Object.keys(files)
      .filter((name) => name.startsWith('xl/worksheets/') && name.endsWith('.xml'))
      .sort()[0] ?? 'xl/worksheets/sheet1.xml';

  const sheetXml = files[sheetPath];
  if (sheetXml === undefined) return { rows: [], sheetName: '' };

  const workbookXml = files['xl/workbook.xml'];
  const sheetName =
    workbookXml === undefined
      ? 'Planilha 1'
      : (/<sheet[^>]*name="([^"]*)"/.exec(strFromU8(workbookXml))?.[1] ?? 'Planilha 1');

  return {
    rows: parseSheet(strFromU8(sheetXml), shared, {
      porcentagens: estilosDe(files),
      aba: decodeEntities(sheetName),
    }),
    sheetName: decodeEntities(sheetName),
  };
}

/**
 * Todas as abas da pasta, na ordem em que o arquivo as declara.
 *
 * `readXlsx` devolve a primeira porque a importação de catálogo sempre foi de uma tabela
 * só. Há planilha que usa **uma aba por assunto** — a de serviços da obra tem uma por
 * tipologia — e ali a primeira aba não é a resposta, é um oitavo dela.
 *
 * A ordem vem do `workbook.xml`, e não do nome do arquivo interno: `sheet10.xml` ordenado
 * como texto vem antes de `sheet2.xml`, e as abas apareceriam embaralhadas.
 *
 * `linhasPorAba` troca o limite de linhas com dado (`LINHAS_COM_DADO_POR_ABA`); existe para o
 * teste não precisar montar duzentas mil linhas para ver a recusa.
 */
export function readXlsxSheets(
  bytes: Uint8Array,
  opcoes: { readonly linhasPorAba?: number } = {},
): readonly XlsxContent[] {
  const files = unzipSync(bytes);

  const sharedXml = files['xl/sharedStrings.xml'];
  const shared = sharedXml === undefined ? [] : parseSharedStrings(strFromU8(sharedXml));

  const workbookXml = files['xl/workbook.xml'];
  const nomes =
    workbookXml === undefined
      ? []
      : [...strFromU8(workbookXml).matchAll(/<sheet[^>]*name="([^"]*)"/g)].map((achado) =>
          decodeEntities(achado[1] ?? ''),
        );

  const caminhos = Object.keys(files)
    .filter((name) => name.startsWith('xl/worksheets/') && name.endsWith('.xml'))
    .sort((a, b) => numeroDaAba(a) - numeroDaAba(b));

  const porcentagens = estilosDe(files);

  return caminhos.map((caminho, indice) => {
    const sheetXml = files[caminho];
    const sheetName = nomes[indice] ?? `Planilha ${String(indice + 1)}`;
    return {
      rows:
        sheetXml === undefined
          ? []
          : parseSheet(strFromU8(sheetXml), shared, {
              porcentagens,
              aba: sheetName,
              ...(opcoes.linhasPorAba === undefined ? {} : { limite: opcoes.linhasPorAba }),
            }),
      sheetName,
    };
  });
}

/** `xl/worksheets/sheet10.xml` → 10. Sem isso, a ordenação textual põe a 10 antes da 2. */
function numeroDaAba(caminho: string): number {
  return Number(/sheet(\d+)\.xml$/.exec(caminho)?.[1] ?? 0);
}

/** Assinatura do zip — `PK\x03\x04`. É por ela que o formato é decidido, não pela extensão. */
export function looksLikeZip(bytes: Uint8Array): boolean {
  return bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04;
}
